/**
 * Connection health enums - SOURCE OF TRUTH
 * Pure TypeScript - no Drizzle imports
 *
 * One vocabulary for every third-party connection we hold on a customer's
 * behalf, whatever the provider. The question the model answers is not "is the
 * token valid" but "can we still act for this customer" — which is why Stripe,
 * whose failure mode is unfinished onboarding rather than a dead credential,
 * fits the same three fields as a revoked Facebook session.
 */
/**
 * The connections we hold. One entry per credential we can independently lose:
 * a Page token and an Instagram-Login token die separately even though both
 * say "Meta" to the customer, and the audit of 2026-08-24 found orgs where
 * exactly one of the two was dead.
 *
 * `meta_ads` is the connecting user's own token, which the Page tokens are
 * minted from. It looks redundant with `meta_page` until you count: seven
 * production organisations have a Meta Ads integration and no active Page —
 * two configured, five that opened the connect flow and never finished it, one
 * of those sitting since April. Without this entry none of the seven is
 * probed by anything.
 */
export const connectionProviderLabels = {
    meta_ads: 'Meta Ads',
    meta_page: 'Facebook Page',
    meta_instagram: 'Instagram',
    meta_whatsapp: 'WhatsApp',
    stripe_connect: 'Stripe',
};
export const connectionProviderValues = Object.keys(connectionProviderLabels);
/**
 * What we can currently do with the connection.
 *
 * `degraded` is deliberately distinct from `action_required`: a Stripe account
 * that can charge but has an open business-model review still works today and
 * must not generate a "your integration is broken" alert.
 */
export const connectionHealthStateLabels = {
    healthy: 'Working',
    degraded: 'Working, with a warning',
    action_required: 'Needs attention',
    dead: 'Not working',
};
export const connectionHealthStateValues = Object.keys(connectionHealthStateLabels);
/** States that mean the customer is losing something right now. */
export const BROKEN_CONNECTION_STATES = [
    'action_required',
    'dead',
];
/**
 * What the customer has to do about it — the field that decides which message
 * they get.
 *
 * This exists because a boolean was wrong. Five orgs in the 2026-08-24 audit
 * failed with Meta error 190/464 ("not a confirmed user"): Meta has locked the
 * person's Facebook account, and reconnecting in our app CANNOT fix it. Sending
 * those orgs a reconnect button fails and makes us look broken. Eleven Stripe
 * orgs need `finish_setup`, which is not a reconnect either.
 */
export const connectionHealthRemedyLabels = {
    none: 'Nothing to do',
    reconnect: 'Reconnect the account',
    finish_setup: 'Finish setting up',
    contact_provider: 'Resolve it with the provider',
};
export const connectionHealthRemedyValues = Object.keys(connectionHealthRemedyLabels);
/**
 * Stable, provider-namespaced reason codes.
 *
 * These are the classification target for every provider error: a probe never
 * rethrows, it maps to one of these. Keep them stable — they are queried in
 * PostHog and read by humans in Slack.
 *
 * `*.unknown` is the catch-all. It is counted and sampled once per run, never
 * logged per-org, because per-org logging of an expected steady state is what
 * made the previous alerter unusable.
 */
export const CONNECTION_REASON_CODES = {
    /** Everything answering normally. */
    OK: 'ok',
    // ── Meta ──────────────────────────────────────────────────────────────
    //
    // Reason codes for Meta API FAILURES are not listed here. They are derived
    // from `meta-error-registry.ts`, which already classifies 92 of them with
    // copy, an action URL and a video guide — `meta.auth_token_revoked`,
    // `meta.user_unconfirmed`, and so on, via `reasonCodeForErrorKey`. Adding a
    // duplicate list here is how the two drift.
    //
    // What remains are the states Meta never tells us about, because they are
    // conditions on OUR side of the connection.
    /** The app is no longer subscribed to the Page/WABA webhooks. */
    META_WEBHOOK_UNSUBSCRIBED: 'meta.webhook_unsubscribed',
    /** We hold no usable credential for a connection that claims to be active. */
    META_CREDENTIALS_MISSING: 'meta.credentials_missing',
    /** The connect flow was opened and abandoned before choosing an account. */
    META_SETUP_INCOMPLETE: 'meta.setup_incomplete',
    /**
     * Meta will not let this WABA START conversations — almost always 141006,
     * a failed payment method on the customer's own WhatsApp Business Account.
     *
     * Nothing about the CONNECTION is wrong, which is why a token probe and a
     * webhook probe both pass. WhatsApp Cloud bills per conversation to the card
     * the clinic holds on their WABA, and only they can fix it.
     */
    META_WHATSAPP_SEND_BLOCKED: 'meta.whatsapp_send_blocked',
    /** Meta reports the number as rate/quality limited but not blocked. */
    META_WHATSAPP_SEND_LIMITED: 'meta.whatsapp_send_limited',
    /**
     * The number was never registered on the Cloud API (Meta 141000 / 133010),
     * so every send is blocked. Reconnecting registers it.
     */
    META_WHATSAPP_NOT_REGISTERED: 'meta.whatsapp_not_registered',
    /** Nothing in the registry matched — sampled, and worth registering. */
    META_UNKNOWN: 'meta.unknown',
    /**
     * The Instagram account is no longer a professional one. A PERSONAL account
     * cannot receive DMs through the API, and the token stays perfectly valid —
     * `account_type` is captured at connect time and, until now, never re-read.
     */
    META_INSTAGRAM_NOT_PROFESSIONAL: 'meta.instagram_not_professional',
    // ── Ours, not the provider's ──────────────────────────────────────────
    /**
     * Subscribed, authenticated, and yet nothing is arriving.
     *
     * Every other check asks whether a webhook SUBSCRIPTION exists. This asks
     * whether events are actually being delivered, which is a different question
     * and the one shape of failure none of the provider probes can see.
     */
    WEBHOOK_SILENT: 'webhook.silent',
    // ── Stripe ────────────────────────────────────────────────────────────
    /** Onboarding started and abandoned — cannot take money. */
    STRIPE_REQUIREMENTS_DUE: 'stripe.requirements_due',
    /** Stripe has disabled charging on the account. */
    STRIPE_CHARGES_DISABLED: 'stripe.charges_disabled',
    /** Charging works; payouts do not. Money in, none out. */
    STRIPE_PAYOUTS_DISABLED: 'stripe.payouts_disabled',
    /** Charging fine, but a review or extra info is outstanding. */
    STRIPE_REVIEW_PENDING: 'stripe.review_pending',
    /**
     * STRIPE has closed the door — `rejected.*` or `listed`. There is nothing for
     * the customer to finish and nothing we can do; only Stripe can lift it.
     */
    STRIPE_ACCOUNT_REJECTED: 'stripe.account_rejected',
    /** Stripe is verifying. Nothing to do but wait — never alert on this. */
    STRIPE_UNDER_REVIEW: 'stripe.under_review',
    /** The connected account is gone from Stripe's side. */
    STRIPE_ACCOUNT_MISSING: 'stripe.account_missing',
    STRIPE_UNKNOWN: 'stripe.unknown',
};
// ── Channel status (ENG-1944) ──────────────────────────────────────────────
/**
 * The five things an owner means by "is Instagram connected", one per code
 * path. They are NOT the providers above: Instagram posting and the Instagram
 * chatbot can run on different records (a Facebook Page's linked account for
 * one, the standalone Instagram login for the other), so one can work while the
 * other cannot. Settings and Claire both read `GET /integrations/channel-status`
 * keyed by these.
 */
export const connectionChannelLabels = {
    facebook_posting: 'Facebook posting',
    instagram_posting: 'Instagram posting',
    instagram_chatbot: 'Instagram chatbot',
    messenger: 'Messenger chatbot',
    whatsapp: 'WhatsApp chatbot',
};
export const connectionChannelValues = Object.keys(connectionChannelLabels);
export const channelStatusLabels = {
    connected: 'Working',
    needs_reconnect: 'Needs reconnecting',
    not_connected: 'Not connected',
};
export const channelStatusValues = Object.keys(channelStatusLabels);
/**
 * The record a channel's code path actually reads. Naming it is the point: an
 * owner told "reconnect Instagram" while Settings shows Instagram connected
 * needs to hear WHICH Instagram connection is missing.
 */
export const channelRecordLabels = {
    meta_page: 'Facebook Page',
    meta_page_linked_instagram: 'Instagram account linked to a Facebook Page',
    instagram_login: 'Instagram login (standalone)',
    whatsapp_account: 'WhatsApp Business number',
};
export const channelRecordValues = Object.keys(channelRecordLabels);
/** The button the Settings channel list shows for a channel that is not working. */
export const channelFixActionValues = [
    'connect_meta',
    'reconnect_meta',
    'link_instagram_to_page',
    'reconnect_instagram',
    'connect_whatsapp',
    'reconnect_whatsapp',
    'contact_provider',
];
//# sourceMappingURL=connection-health.js.map